import { Box, CircularProgress, IconButton, InputBase, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import React, { useState } from 'react'
import { Helmet } from 'react-helmet';
import { white } from '@styles/colors';
import { Search } from '@mui/icons-material';
import EmployersListingRow from '@features/superAdmin/components/EmployersListingRow';
import { useGetBlockAccounts } from '@features/superAdmin/hooks/useGetBlockAccounts';
import useDebounce from '@hooks/UseDebounce';


const BlockAccounts = () => {

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchValue, setSearchValue] = useState('');
    const [columnNo, setColumnNo] = useState(0);
    const [sortedDirection, setSortedDirection] = useState('asc');
    const debounceValue = useDebounce(searchValue, 500);
    const [applicationsListAlertText, setApplicationsListAlertText] = useState<string>('');

    const { employers, isLoadingEmployers } = useGetBlockAccounts({
        searchValue: debounceValue,
        page,
        columnNo,
        sortedDirection,
        page_length: rowsPerPage,
        setAlertText: setApplicationsListAlertText,
    });

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };


    const handleSort = (column: number) => {
        const isAsc = columnNo === column && sortedDirection === 'asc';
        setSortedDirection(isAsc ? 'desc' : 'asc');
        setColumnNo(column);
    };

    const renderSortArrow = (column: number) => {
        if (columnNo === column) {
            return sortedDirection === 'asc' ? (
                <Stack direction='row' >
                    <NorthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            ) : (
                <Stack direction='row'>
                    <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            );
        }
        return (
            <Stack direction='row'>
                <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
            </Stack>
        );
    };

    return (
        <div style={{marginBottom:'40px'}}>
            <Helmet>
                <title>Admin Blocked Accounts</title>
            </Helmet>
            <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
                <Stack p={3} borderRadius='8px' bgcolor={white} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                    <Typography variant="h3" fontSize="28px" fontWeight="bold">
                        Blocked Accounts
                    </Typography>
                </Stack>
                <Stack spacing={4} sx={{ borderRadius: 5 }} direction={{ xs: 'column', sm: 'row' }} justifyContent={{ sm: 'space-between' }} alignItems="center">
                    <Paper onSubmit={(e) => e.preventDefault()} component="form" sx={{ pl: '10px', display: 'flex', alignItems: 'center', maxWidth: 400, width: '100%', borderRadius: 5 }}>
                        <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder="Search"
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchValue}
                            onChange={(e) => {
                                setSearchValue(e.target.value)
                                setPage(1)
                            }}
                        />
                        <IconButton type="button" sx={{ px: '10px' }} aria-label="search">
                            <Search />
                        </IconButton>
                    </Paper>
                </Stack>
                <TableContainer component={Paper}>
                    <Table >
                        <TableHead>
                            <TableRow >
                                <TableCell onClick={() => handleSort(3)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Business</b> {renderSortArrow(3)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(0)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Name</b> {renderSortArrow(0)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(1)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Email</b> {renderSortArrow(1)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(6)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Logins</b> {renderSortArrow(6)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(8)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Last Login</b> {renderSortArrow(8)}
                                    </Stack>
                                </TableCell>

                                <TableCell onClick={() => handleSort(4)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Status</b> {renderSortArrow(4)}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingEmployers ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>

                                            <CircularProgress />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : employers?.objects?.length > 0 ? (
                                <>
                                    {employers?.objects?.map(
                                        (row: any) => (
                                            <EmployersListingRow row={row} blockAccount />
                                        ),
                                    )}
                                </>
                            ) : employers?.objects?.length < 1 ? (
                                <TableRow hover >
                                    <TableCell colSpan={6}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>
                                            No Data Found
                                        </Box>
                                    </TableCell>
                                </TableRow>

                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>

                                            {applicationsListAlertText}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={employers?.total_employers || 0}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Stack>
        </div>
    )
}

export default BlockAccounts